// React Imports
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

// I18n
import { Translate, I18n } from 'react-redux-i18n';

// Library to throttle API requests.
import AwesomeDebouncePromise from 'awesome-debounce-promise';

// Spectrum Components
import Autocomplete from '@react/react-spectrum/Autocomplete';
import Button from '@react/react-spectrum/Button';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import { Grid, GridRow, GridColumn } from '@react/react-spectrum/Grid';
import Heading from '@react/react-spectrum/Heading';
import Textfield from '@react/react-spectrum/Textfield';
import Tooltip from '@react/react-spectrum/Tooltip';
import Wait from '@react/react-spectrum/Wait';

// API calls
import api from '../../api/apcApi';

// Project Components
import Progress from '../global/Progress';
import { sfLangMapping } from '../../i18n/config';

// Redux Actions
import {
  updateCompanySearchTerm,
  selectCompany,
  saveCompanySuggestions,
  showLoadingIndicator,
  setSuggestionsError,
  setNoMatchingCompanies,
} from '../../actions/companySearchActions';
import { throwError } from '../../actions/errorActions';

// CSS
import '../css/CompanySearch.scss';

/**
 * Component for displaying the Public Domain Company Search Page.
 *
 * This component displays a free-text type-ahead field that shows search results for a company.
 *
 * @component
 * @example
 *
 * return (
 *   <CompanySearchPublic />
 * )
 */
class CompanySearchPublic extends Component {
  constructor(props) {
    super(props);
    // Throttle requests to only run 800ms after the last keystroke.
    this.getSuggestionsDebounced = AwesomeDebouncePromise(this.getSuggestions, 800,
      { leading: true });
  }

  componentDidMount() {
    document.body.addEventListener('click', this.onApplyJoinClick);
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.onApplyJoinClick);
  }

  // Get the company suggestions from the API.
  getSuggestions = (query) => {
    const {
      selectedCompany,
      saveCompanySuggestionsAction,
      showLoadingIndicatorAction,
      throwErrorAction,
      setSuggestionsErrorAction,
      setNoMatchingCompaniesAction,
    } = this.props;

    /* Only fetch if we have a value with greater than 2 characters,
     * not whitespace, and not if a company is already selected.
     */
    if (query && query.match(/^ *$/) === null && query.length > 2 && !selectedCompany) {
      // Show the loading indicator on the textfield.
      showLoadingIndicatorAction();

      // Spectrum Autocomplete expects a promise
      return new Promise((resolve) => {
        // Make the API call
        api.get(`/v1/findpartneraccount?programType=APC&value=${encodeURIComponent(query)}`,
          {
            headers: {
              Authorization: `Bearer ${window.adobeIMS.getAccessToken().token}`,
              'x-api-key': window.apcConfig.adobeIO.apiKey,
              // Prefer: 'example=results',
            },
          }).then((result) => {
          if (result.data.accounts) {
            if (!result.data.accounts.length) {
              // Display no results indicator.
              setNoMatchingCompaniesAction();
              resolve([]);
            } else {
              // Sort alphabetically by name.
              const sortedAccounts = _.sortBy(result.data.accounts, [(o) => o.name]);
              // Save suggestions to Redux Store
              saveCompanySuggestionsAction(sortedAccounts);
              // Resolve
              resolve(sortedAccounts);
            }
          } else {
            // Throw an error.
            setSuggestionsErrorAction();
            throwErrorAction({ messagePath: 'workflow.error_page.company_search_problem' });
            window.console.error('Error: Problem with fetching autocomplete suggestions, no suggestion payload.', result);
            if (window.newrelic) {
              window.newrelic.noticeError('Missing object key: accounts',
                {
                  page: 'Public Domain Company Search',
                  api: '/v1/findpartneraccount',
                  query,
                  fatal: false,
                  user: window.adobeIMS.getUserProfile().email,
                  response: result.data,
                });
            }
            resolve([]);
          }
        }).catch((error) => {
          // API call failed, throw an error.
          setSuggestionsErrorAction();
          throwErrorAction({ messagePath: 'workflow.error_page.company_search_problem' });
          if (window.newrelic) {
            window.newrelic.noticeError(error.message,
              {
                page: 'Public Domain Company Search',
                api: '/v1/findpartneraccount',
                query,
                fatal: false,
                user: window.adobeIMS.getUserProfile().email,
              });
          }
        });
      });
    }
    // If the query is invalid (not long enough or whitespace) don't return any data.
    return [];
  };

  // Renders the currently signed in user's e-mail address for the translation string.
  renderImsEmail = () => window.adobeIMS.getUserProfile().email;

  // Renders an autocomplete item in the dropdown.
  renderAutocompleteItem = (item) => (
    <div className="autocomplete-itemRow">
      <span className="autocomplete-company-name">{item.name}</span>
      <br />
      <span className="autocomplete-country">{item.country}</span>
    </div>
  );

  // If we are currently fetching suggestions, show a spinner icon.
  renderWaitIcon = () => {
    const { fetchingSuggestions } = this.props;
    if (fetchingSuggestions) {
      return <Wait className="loadingSuggestions" size="S" />;
    }
    return null;
  }

  // Get the current search term from the redux store.
  getSearchTerm = () => {
    const { companySearchTerm } = this.props;
    return companySearchTerm;
  }

  // Sets the new search term.
  updateSearchTerm = (value) => {
    const { updateCompanySearchTermAction, selectedCompany, selectCompanyAction } = this.props;
    updateCompanySearchTermAction(value);

    // If we have a selected company and the value changes, then clear the company.
    if (selectedCompany && value !== selectedCompany.name) {
      selectCompanyAction(null);
    }
  }

  // Handle the clicking of the Start Enrollment button
  handleSubmit = () => {
    const { history, selectedCompany } = this.props;
    if (selectedCompany) {
      history.push('/personal_information');
    }
  };

  // Handle the selection of a autocomplete item
  handleSelectItem = (company) => {
    const { selectCompanyAction, updateCompanySearchTermAction } = this.props;
    selectCompanyAction(company);
    updateCompanySearchTermAction(company.name);
    document.activeElement.blur();
  };

  // Decides whether we can enable the submit button.
  renderSubmitButton = () => {
    const { selectedCompany } = this.props;
    if (selectedCompany) {
      return false;
    }
    return true;
  };

  // Renders a checkmark on the input when a company has been selected.
  renderValidationState = () => {
    const { selectedCompany } = this.props;
    if (selectedCompany) {
      return 'valid';
    }
    return null;
  };

  renderTooltip = () => {
    const { noMatchingCompanies } = this.props;
    if (noMatchingCompanies) {
      return (
        <div
          className="no-results-tooltip"
        >
          <Tooltip
            open
            placement="bottom"
            role="tooltip"
            variant="inspect"
          >
            <Translate value="workflow.find_company.no_matches_search" />
          </Tooltip>
        </div>
      );
    }
    return null;
  }

  onApplyJoinClick = (event) => {
    if (event.target.id === 'apply-join') {
      event.preventDefault();
      const { locale } = this.props;
      const sfLocale = Object.keys(sfLangMapping).includes(locale)
        ? sfLangMapping[locale]
        : 'en_US';
      const profile = window.adobeIMS.getUserProfile();
      const guid = profile.userId;
      const enrollLink = `${window.apcConfig.partner.enrollment}?lang=${sfLocale}&fn=${profile.first_name}&ln=${profile.last_name}&em=${profile.email}&aid=${profile.email}&guid=${guid}&countryCode=${profile.countryCode}`;
      window.open(enrollLink, '_self');
    }
  };

  render() {
    return (
      <div className="page-companySearchPublic">
        <Progress step={1} />
        <div className="page-container">
          <Heading>
            <Translate value="workflow.find_company.header" />
          </Heading>
          <p>
            <Translate value="workflow.find_company.public_domain_company_search" email={this.renderImsEmail()} />
          </p>
          <Grid variant="fixed" className="field-grid">
            <GridRow>
              <GridColumn
                size={[12, 6]}
              >
                <div className="companySearch-autocomplete">
                  <FieldLabel
                    label={I18n.t('workflow.find_company.company_name')}
                    necessityIndicator="icon"
                    labelFor="companySearch"
                  >
                    {this.renderWaitIcon()}
                    <Autocomplete
                      allowCreate={false}
                      renderItem={(item) => this.renderAutocompleteItem(item)}
                      getCompletions={this.getSuggestionsDebounced}
                      value={this.getSearchTerm()}
                      onSelect={(item) => this.handleSelectItem(item)}
                    >
                      <Textfield
                        autoFocus
                        disabled={false}
                        invalid={false}
                        placeholder={I18n.t('workflow.find_company.enter_company_name')}
                        quiet={false}
                        readOnly={false}
                        required={false}
                        onChange={this.updateSearchTerm}
                        validationState={this.renderValidationState()}
                      />
                    </Autocomplete>
                  </FieldLabel>
                  {this.renderTooltip()}
                </div>
              </GridColumn>
            </GridRow>
          </Grid>
          <div className="button-container">
            <Button variant="cta" disabled={this.renderSubmitButton()} onClick={this.handleSubmit}><Translate value="workflow.find_company.start_enrollment" /></Button>
          </div>
          <p>
            <Translate dangerousHTML value="workflow.find_company.company_not_listed" />
          </p>
        </div>
      </div>
    );
  }
}
CompanySearchPublic.propTypes = {
  /**
   * companySearchTerm {String} The search term, provided by mapStateToProps.
   */
  companySearchTerm: PropTypes.string,
  /**
   * updateCompanySearchTermAction {Function} Redux action to update the search term,
   * provided by mapDispatchToProps.
   */
  updateCompanySearchTermAction: PropTypes.func.isRequired,
  /**
   * fetchingSuggestions {Boolean} Whether we are currently fetching autocomplete
   * suggestions, provided by mapStateToProps.
   */
  fetchingSuggestions: PropTypes.bool.isRequired,
  /**
   * selectedCompany {Object} The currently selected company, provided by mapStateToProps.
   * TODO: Update with the final object shape once API is finished.
   */
  selectedCompany: PropTypes.shape({
    recId: PropTypes.string,
    name: PropTypes.string,
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    postalCode: PropTypes.string,
  }),
  /**
   * selectCompanyAction {Function} Redux action to set the selected company. Provided by
   * mapDispatchToProps.
   */
  selectCompanyAction: PropTypes.func.isRequired,
  /**
   * history {Object} The history object provided by React Router. Used to navigate to other pages.
   */
  history: PropTypes.shape({
    history: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object,
    push: PropTypes.func,
  }).isRequired,
  /**
   * noMatchingCompanies {Boolean} True when no matching companies are found,
   * provided by mapStateToProps.
   */
  noMatchingCompanies: PropTypes.bool.isRequired,
  /**
   * saveCompanySuggestionsAction {Function} Redux action to save the company
   * autocomplete suggestions.Provided by mapDispatchToProps.
   */
  saveCompanySuggestionsAction: PropTypes.func.isRequired,
  /**
   * showLoadingIndicatorAction {Function} Redux action to show the loading
   * indicator. Provided by mapDispatchToProps.
   */
  showLoadingIndicatorAction: PropTypes.func.isRequired,
  /**
   * throwErrorAction {Function} Redux action to throw an error message.
   * Provided by mapDispatchToProps.
   */
  throwErrorAction: PropTypes.func.isRequired,
  /**
   * setSuggestionsErrorAction {Function} Redux action to clear the loading
   * indicator and suggestions. Provided by mapDispatchToProps.
   */
  setSuggestionsErrorAction: PropTypes.func.isRequired,
  /**
   * setNoMatchingCompaniesAction {Function} Redux action to display the
   * no matching companies tooltip. Provided by mapDispatchToProps.
   */
  setNoMatchingCompaniesAction: PropTypes.func.isRequired,
  /**
   * locale {String} Currently selected locale. Provided by mapStateToProps.
   */
  locale: PropTypes.string.isRequired,
};
CompanySearchPublic.defaultProps = {
  companySearchTerm: '',
  selectedCompany: null,
};

const mapStateToProps = (state) => ({
  selectedCompany: state.company.selectedCompany,
  fetchingSuggestions: state.company.fetchingSuggestions,
  autocompleteSuggestions: state.company.suggestions,
  companySearchTerm: state.company.companySearchTerm,
  noMatchingCompanies: state.company.noMatchingCompanies,
  locale: state.i18n.locale,
});

const mapDispatchToProps = {
  updateCompanySearchTermAction: updateCompanySearchTerm,
  selectCompanyAction: selectCompany,
  saveCompanySuggestionsAction: saveCompanySuggestions,
  showLoadingIndicatorAction: showLoadingIndicator,
  throwErrorAction: throwError,
  setSuggestionsErrorAction: setSuggestionsError,
  setNoMatchingCompaniesAction: setNoMatchingCompanies,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanySearchPublic);